/**
 * Reiter *Zahlungsmatrix* (`matrix`, Spec 08) — Verträge als Zeilen, sechs
 * Monate als Spalten, `GET /billing/matrix`. Zeigt auf einen Blick, wer wann
 * wie viel offen hat, über den ganzen sichtbaren Bestand statt Posten für
 * Posten (das leistet bereits *Offene Posten*, Spec 04a).
 *
 * **Reine Anzeige** (Spec 08, „Nicht enthalten"): keine bearbeitbaren Zellen,
 * keine eigene Rasternavigation. Das einzige Interaktive ist der Klick auf
 * eine Kachel mit offenem Rest — er öffnet **dasselbe** `PaymentSheet` wie
 * die Liste offener Posten, über `onPay` nach oben gereicht (`routes/Geld.tsx`
 * besitzt das Sheet, „Ein Container pro Bildschirm besitzt die Queries").
 *
 * **Die zweite Ausnahme von „keine flächige Einfärbung"** (`design-system`,
 * „Farbe", Spec 08 Offene Frage 1): Jede Kachel trägt ihre Statusfarbe als
 * Tönung von höchstens 10 % (`--color-status-*-surface` in `tokens.css`,
 * `color-mix`) — **immer zusammen mit dem Statuswort**, Farbe ist nie das
 * einzige Signal. `open` und `none` bleiben ungetönt.
 *
 * **Zwei Layoutformen, dieselben `data-testid`** (Spec 08, „Unter 768 px"):
 * ein Raster ab 768 px, darunter eine Karte je Vertrag. Über
 * `useIsDesktopViewport()` entschieden, nicht per CSS `hidden md:block` —
 * zwei nur optisch versteckte Kopien stünden mit denselben Bezeichnern
 * doppelt im Dokument (`lib/viewport.ts`).
 *
 * **Zeilen kommen seitenweise** (`useInfiniteQuery` in `features/billing/api.ts`,
 * `matrix-more`, AK-482); `columns` und die Spaltensummen sind über alle
 * Seiten identisch (AK-479, gehören zur **ganzen sichtbaren Menge**, nicht
 * zur ausgelieferten Seite) — deshalb kommen sie ausschließlich von der
 * ersten geladenen Seite.
 */

import type { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query'

import { EmptyState } from '@/components/states/EmptyState'
import { ErrorState } from '@/components/states/ErrorState'
import { useIsDesktopViewport } from '@/lib/viewport'
import type { MatrixCell, MatrixColumn, MatrixPage, MatrixRow } from '@/features/billing/api'
import type { PaymentSheetPrefill } from '@/features/billing/PaymentSheet'
import { FIELD_CLASS } from '@/components/FormField'

interface PaymentMatrixProps {
  readonly query: UseInfiniteQueryResult<InfiniteData<MatrixPage>>
  /** `search.von` aus der URL (Spec 08, „Zustand in der URL") — nur zum
   * Weiterreichen an `onNavigate`; die Kopfzeile selbst liest das geladene
   * Fenster aus `query`, nicht aus dieser Prop, weil der Server bei
   * `von=undefined` seine eigene Vorgabe liefert (laufender Monat). */
  readonly onNavigate: (von: string) => void
  readonly onPay: (prefill: PaymentSheetPrefill) => void
}

/** `"2026-04-01"` → `"2026-04"`, der Schlüssel in jedem `matrix-*`-Bezeichner
 * (Spec 08, „Die Kachel"). */
function monthKey(iso: string): string {
  return iso.slice(0, 7)
}

/** `"2026-04-01"` um `delta` Monate verschoben, als `"YYYY-MM"` — reine
 * Kalendernavigation für `matrix-prev`/`matrix-next` (Spec 08, AK-485), keine
 * Fachregel: Die Fensterbildung selbst (welcher Monat der letzte sein darf)
 * entscheidet ausschließlich der Server, hier wird nur der URL-Parameter für
 * den nächsten Aufruf gebildet. */
function shiftMonth(iso: string, delta: number): string {
  const year = Number(iso.slice(0, 4))
  const month = Number(iso.slice(5, 7))
  const total = year * 12 + (month - 1) + delta
  const nextYear = Math.floor(total / 12)
  const nextMonth = (total % 12) + 1
  return `${String(nextYear)}-${String(nextMonth).padStart(2, '0')}`
}

/** Die Tönung einer Kachel — höchstens 10 % aus dem Statustoken (Spec 08,
 * „Die Kachel"). `open` und `none` bleiben ungetönt (`bg-surface`). */
const STATUS_SURFACE: Readonly<Record<string, string>> = {
  overdue: 'bg-status-overdue-surface',
  partial: 'bg-status-partial-surface',
  settled: 'bg-status-paid-surface',
}

function statusSurfaceClass(value: string): string {
  return STATUS_SURFACE[value] ?? 'bg-surface'
}

const CONTRACT_COL = 'w-[232px] min-w-[232px] shrink-0'
const MONTH_COL = 'w-[120px] min-w-[120px] shrink-0'

/** Punktmarkierung + Statuswort einer Kachel — wortgleich in Raster- und
 * Kartenform (Spec 08, „Die Kachel"), der Punkt nur bei `overdue`. */
function StatusLabel({ status }: { readonly status: MatrixCell['status'] }) {
  return (
    <span className="text-label text-muted flex items-center gap-1">
      {status.value === 'overdue' && (
        <span aria-hidden="true" className="bg-status-overdue size-1.5 shrink-0 rounded-full" />
      )}
      {status.label}
    </span>
  )
}

export function PaymentMatrix({ query, onNavigate, onPay }: PaymentMatrixProps) {
  const isDesktop = useIsDesktopViewport()

  const pages = query.data?.pages ?? []
  const columns = pages[0]?.columns ?? []
  const rows = pages.flatMap((page) => page.rows)
  const totalRows = pages[0]?.total_rows ?? 0
  const firstColumn = columns[0]
  const lastColumn = columns[columns.length - 1]

  function handlePrev() {
    if (firstColumn === undefined) return
    onNavigate(shiftMonth(firstColumn.month, -1))
  }

  // Ob ein Fenster einen Monat später noch gültig wäre, ist eine Serverregel
  // (AK-471: 422, sobald das Fenster nach dem laufenden Monat endet) — der
  // Client liest dafür nicht mehr die Geräteuhr (CLAUDE.md Regel 2, Regel 4;
  // Befund W6). `has_next` kommt wie `columns`/`total_rows` ausschließlich
  // von der ersten geladenen Seite, weil es fürs ganze Fenster gilt.
  const canGoNext = pages[0]?.has_next ?? false

  function handleNext() {
    // `von` ist der **erste** Monat des Fensters (Spec 08, „Zustand in der
    // URL") — verschoben wird deshalb `firstColumn`, nicht `lastColumn`.
    // Ein Schub von `lastColumn` aus setzte `von` auf einen Monat, der schon
    // im aktuellen Fenster der letzte war, und übersprang damit fünf Monate
    // auf einmal — nach `matrix-prev` gefolgt von `matrix-next` landete das
    // Fenster nicht wieder auf dem Ausgangspunkt, sondern weit dahinter und
    // der Server lehnte es mit 422 ab (Fund `08-zahlungsmatrix.spec.ts`,
    // AK-485).
    if (firstColumn === undefined || !canGoNext) return
    onNavigate(shiftMonth(firstColumn.month, 1))
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="border-border flex h-(--row-h) items-center gap-1 border-b px-(--page-x)">
        <button
          type="button"
          data-testid="matrix-prev"
          aria-label="Ein Monat zurück"
          disabled={firstColumn === undefined}
          onClick={handlePrev}
          className="text-muted hover:text-text focus-visible:outline-accent flex min-h-(--tap-min) min-w-(--tap-min) items-center justify-center focus-visible:outline focus-visible:outline-2 disabled:opacity-40"
        >
          ‹
        </button>
        <span data-testid="matrix-range" className="text-body text-text flex-1 text-center font-medium">
          {firstColumn !== undefined && lastColumn !== undefined
            ? `${firstColumn.label_long} – ${lastColumn.label_long}`
            : ''}
        </span>
        <button
          type="button"
          data-testid="matrix-next"
          aria-label="Ein Monat vor"
          disabled={lastColumn === undefined || !canGoNext}
          onClick={handleNext}
          className="text-muted hover:text-text focus-visible:outline-accent flex min-h-(--tap-min) min-w-(--tap-min) items-center justify-center focus-visible:outline focus-visible:outline-2 disabled:opacity-40"
        >
          ›
        </button>
      </div>

      {query.isPending && <MatrixSkeleton />}

      {query.isError && (
        <ErrorState
          testId="matrix-error"
          retryTestId="matrix-error-retry"
          error={query.error}
          onRetry={() => {
            void query.refetch()
          }}
        />
      )}

      {query.isSuccess && rows.length === 0 && (
        <EmptyState
          testId="matrix-empty"
          title="Für diesen Zeitraum gibt es keine Verträge."
          hint="Wähle einen früheren Zeitraum."
          action={
            <button
              type="button"
              data-testid="matrix-empty-prev"
              onClick={handlePrev}
              className={`text-text px-4 ${FIELD_CLASS}`}
            >
              Ein Monat zurück
            </button>
          }
        />
      )}

      {query.isSuccess && rows.length > 0 && (
        <>
          {isDesktop ? (
            <MatrixTable columns={columns} rows={rows} onPay={onPay} />
          ) : (
            <MatrixCards columns={columns} rows={rows} onPay={onPay} />
          )}

          {query.hasNextPage && (
            <div className="border-border flex justify-center border-t px-(--page-x) py-2">
              <button
                type="button"
                data-testid="matrix-more"
                disabled={query.isFetchingNextPage}
                aria-busy={query.isFetchingNextPage}
                onClick={() => {
                  void query.fetchNextPage()
                }}
                className={`text-text px-4 ${FIELD_CLASS}`}
              >
                Weitere Verträge laden ({rows.length} von {totalRows})
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

/** Laden — Skelett in der Zielform: sechs Zeilen × sechs Spalten, die
 * Vertragsspalte breiter (Spec 08, „Drei Zustände"). Kein Spinner. */
function MatrixSkeleton() {
  return (
    <div data-testid="matrix-skeleton" aria-busy="true" aria-live="polite" className="flex flex-1 flex-col">
      <span className="sr-only">Wird geladen</span>
      {Array.from({ length: 6 }, (_, rowIndex) => (
        <div key={rowIndex} className="border-border flex h-(--matrix-row-h) items-center gap-3 border-b px-(--page-x)">
          <div className="bg-border h-2 w-40 shrink-0 animate-pulse rounded-full" />
          {Array.from({ length: 6 }, (_, colIndex) => (
            <div key={colIndex} className="bg-border h-2 flex-1 animate-pulse rounded-full" />
          ))}
        </div>
      ))}
    </div>
  )
}

// ── Ab 768 px: das Raster ──────────────────────────────────────────────────

interface MatrixLayoutProps {
  readonly columns: readonly MatrixColumn[]
  readonly rows: readonly MatrixRow[]
  readonly onPay: (prefill: PaymentSheetPrefill) => void
}

/**
 * Sechs Spalten plus Vertragsspalte ergeben 952 px (Spec 08, „Das Raster ab
 * 768 px") — `w-fit` lässt den Rasterkörper über die verfügbare Breite hinaus
 * wachsen, `min-w-full` hält ihn bei viel Platz auf voller Breite. Der
 * äußere `overflow-auto` trägt beide Scrollrichtungen zugleich: waagerecht
 * für die Spalten (AK-493), senkrecht für viele Zeilen — `sticky` auf
 * Kopfzeile und Vertragsspalte wirkt dabei relativ zu genau diesem einen
 * Behälter, nicht zu zwei verschachtelten (sonst zöge das eine Sticky-Achse
 * gegen die andere).
 */
function MatrixTable({ columns, rows, onPay }: MatrixLayoutProps) {
  return (
    <div className="min-h-0 flex-1 overflow-auto">
      <div className="flex w-fit min-w-full flex-col">
        <div className="border-border bg-surface sticky top-0 z-10 flex border-b">
          <div
            className={`border-border bg-surface sticky left-0 z-20 flex items-center border-r px-3 ${CONTRACT_COL}`}
          >
            <span className="text-label text-muted">Vertrag</span>
          </div>
          {columns.map((column) => (
            <div
              key={column.month}
              aria-label={column.label_long}
              className={`text-label text-muted flex items-center justify-end px-2 ${MONTH_COL}`}
            >
              {column.label}
            </div>
          ))}
        </div>

        {rows.map((row) => (
          <MatrixTableRow key={row.contract_id} row={row} columns={columns} onPay={onPay} />
        ))}

        <div data-testid="matrix-footer" className="border-border flex border-t">
          <div className={`border-border bg-surface sticky left-0 z-10 flex items-center border-r px-3 ${CONTRACT_COL}`}>
            <span className="text-body text-text font-(--font-weight-section)">Offen je Monat</span>
          </div>
          {columns.map((column) => (
            <div
              key={column.month}
              data-testid={`matrix-footer-${monthKey(column.month)}`}
              data-numeric
              className={`text-body text-text flex items-center justify-end px-2 font-medium ${MONTH_COL}`}
            >
              {column.open_display ?? '—'}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function MatrixTableRow({
  row,
  columns,
  onPay,
}: {
  readonly row: MatrixRow
  readonly columns: readonly MatrixColumn[]
  readonly onPay: (prefill: PaymentSheetPrefill) => void
}) {
  return (
    <div className="border-border flex h-(--matrix-row-h) border-b last:border-b-0">
      <div
        className={`border-border bg-surface sticky left-0 z-10 flex min-w-0 items-baseline gap-1 truncate border-r px-3 ${CONTRACT_COL}`}
      >
        <span className="text-body text-text min-w-0 truncate font-medium">{row.tenant.label}</span>
        <span className="text-label text-muted min-w-0 truncate"> · {row.subject.label}</span>
      </div>
      {columns.map((column, index) => {
        const cell = row.cells[index]
        if (cell === undefined) return null
        return (
          <MatrixTableCell
            key={column.month}
            contractId={row.contract_id}
            tenant={row.tenant}
            cell={cell}
            onPay={onPay}
          />
        )
      })}
    </div>
  )
}

function MatrixTableCell({
  contractId,
  tenant,
  cell,
  onPay,
}: {
  readonly contractId: string
  readonly tenant: MatrixRow['tenant']
  readonly cell: MatrixCell
  readonly onPay: (prefill: PaymentSheetPrefill) => void
}) {
  const testId = `matrix-cell-${contractId}-${monthKey(cell.month)}`

  if (cell.status.value === 'none') {
    return (
      <div
        data-testid={testId}
        role="img"
        aria-label="Keine Sollstellung"
        className={`text-muted flex items-center justify-center ${MONTH_COL}`}
      >
        —
      </div>
    )
  }

  const content = (
    <span className="flex flex-col items-end gap-0.5 px-2 py-1">
      {cell.open_display !== null && (
        <span data-numeric className="text-body text-text font-medium">
          {cell.open_display}
        </span>
      )}
      <StatusLabel status={cell.status} />
    </span>
  )

  // Nur Kacheln mit offenem Rest sind anklickbar (Spec 08, Fachregel 5,
  // AK-486/AK-488) — `settled` trägt `open_cents = 0` und landet damit hier,
  // ohne eigenen Status-Vergleich.
  if (cell.open_cents <= 0) {
    return (
      <div data-testid={testId} className={`flex items-center justify-end ${statusSurfaceClass(cell.status.value)} ${MONTH_COL}`}>
        {content}
      </div>
    )
  }

  return (
    <button
      type="button"
      data-testid={testId}
      onClick={() => {
        onPay({ tenantId: tenant.id, tenantLabel: tenant.label, chargeId: cell.charge_id, openCents: cell.open_cents })
      }}
      className={`focus-visible:outline-accent flex items-center justify-end focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 ${statusSurfaceClass(cell.status.value)} ${MONTH_COL}`}
    >
      {content}
    </button>
  )
}

// ── Unter 768 px: eine Karte je Vertrag ────────────────────────────────────

/** Dieselben `data-testid` wie `MatrixTable` (Spec 08, „Unter 768 px": „Die
 * Journey beschreibt beide Breiten mit denselben Namen") — nur die Form
 * ändert sich, vom Raster zur Karte. */
function MatrixCards({ columns, rows, onPay }: MatrixLayoutProps) {
  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <ul>
        {rows.map((row) => (
          <li key={row.contract_id} data-testid={`matrix-row-${row.contract_id}`} className="border-border border-b px-(--page-x) py-2">
            <p className="text-body mb-1">
              <span className="text-text font-medium">{row.tenant.label}</span>
              <span className="text-muted"> · {row.subject.label}</span>
            </p>
            {columns.map((column, index) => {
              const cell = row.cells[index]
              if (cell === undefined) return null
              return (
                <MatrixCardCell
                  key={column.month}
                  contractId={row.contract_id}
                  tenant={row.tenant}
                  column={column}
                  cell={cell}
                  onPay={onPay}
                />
              )
            })}
          </li>
        ))}
      </ul>

      <div data-testid="matrix-footer" className="border-border border-t px-(--page-x) py-2">
        <p className="text-label text-muted mb-1">Offen je Monat</p>
        <ul>
          {columns.map((column) => (
            <li key={column.month} className="flex min-h-(--tap-min) items-center justify-between gap-2">
              <span className="text-body text-text">{column.label}</span>
              <span
                data-testid={`matrix-footer-${monthKey(column.month)}`}
                data-numeric
                className="text-body text-text font-medium"
              >
                {column.open_display ?? '—'}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

function MatrixCardCell({
  contractId,
  tenant,
  column,
  cell,
  onPay,
}: {
  readonly contractId: string
  readonly tenant: MatrixRow['tenant']
  readonly column: MatrixColumn
  readonly cell: MatrixCell
  readonly onPay: (prefill: PaymentSheetPrefill) => void
}) {
  const testId = `matrix-cell-${contractId}-${monthKey(cell.month)}`

  if (cell.status.value === 'none') {
    return (
      <div data-testid={testId} role="img" aria-label="Keine Sollstellung" className="flex min-h-(--tap-min) items-center justify-between gap-2 px-2">
        <span className="text-body text-text">{column.label}</span>
        <span className="text-body text-muted">—</span>
      </div>
    )
  }

  // Statuswort **vor** dem Betrag (W8): Beide teilen sich einen Behälter, der
  // per `justify-between` rechtsbündig an den Rand der Karte gedrückt wird —
  // stünde der Betrag zuerst, verschöbe die Länge des Statusworts seine
  // Position um bis zu 60 px („Offen" gegen „Teilweise bezahlt"). Als
  // letztes Kind liegt sein rechter Rand dagegen immer an derselben Stelle.
  const content = (
    <>
      <span className="text-body text-text">{column.label}</span>
      <span className="flex items-center gap-2">
        <StatusLabel status={cell.status} />
        {cell.open_display !== null && (
          <span data-numeric className="text-body text-text font-medium">
            {cell.open_display}
          </span>
        )}
      </span>
    </>
  )

  if (cell.open_cents <= 0) {
    return (
      <div data-testid={testId} className={`flex min-h-(--tap-min) items-center justify-between gap-2 px-2 ${statusSurfaceClass(cell.status.value)}`}>
        {content}
      </div>
    )
  }

  return (
    <button
      type="button"
      data-testid={testId}
      onClick={() => {
        onPay({ tenantId: tenant.id, tenantLabel: tenant.label, chargeId: cell.charge_id, openCents: cell.open_cents })
      }}
      className={`focus-visible:outline-accent flex min-h-(--tap-min) w-full items-center justify-between gap-2 px-2 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 ${statusSurfaceClass(cell.status.value)}`}
    >
      {content}
    </button>
  )
}
